import { Settings } from '../persist.js';

/**
 * VOIDLINE audio: 100% synthesized WebAudio — engine, wind, SFX, positional rival
 * hums and a generative electronic soundtrack. No audio files. Context unlocks on
 * first user gesture (browser autoplay policy), everything degrades silently
 * before that.
 *
 * The soundtrack runs on a lookahead scheduler: a single ~10Hz interval schedules
 * every 16th note up to 200ms ahead on the AudioContext clock, so note timing is
 * sample-accurate and never drifts the way per-step setInterval callbacks do.
 * Structure is a 16-bar cycle of intro → verse → lift → drop (4 bars each) with
 * risers/fills into section changes, sidechain-style pumping (pads/bass duck on
 * every kick) and a deterministic per-seed key: scale, root and chord progression
 * are all derived from the seed, so each track has its own consistent mood — and
 * jingle SFX reuse the same key so they never clash with the music.
 */

type SfxName =
  | 'ui_move'
  | 'ui_confirm'
  | 'ui_back'
  | 'count_beep'
  | 'count_go'
  | 'checkpoint'
  | 'lap'
  | 'best_lap'
  | 'boost_fire'
  | 'boost_pad'
  | 'wall_hit'
  | 'ship_hit'
  | 'land'
  | 'respawn'
  | 'finish'
  | 'eliminated'
  | 'draft';

type MusicMode = 'menu' | 'race';
type MusicSection = 'intro' | 'verse' | 'lift' | 'drop';

/** Positional rival-engine input; world coordinates match the renderer/physics. */
export interface NearbyShip {
  x: number;
  y: number;
  z: number;
  speed: number;
  boosting: boolean;
}

/** Listener pose for positional audio: camera/ship position + orthonormal fwd/up. */
export interface ListenerPose {
  x: number;
  y: number;
  z: number;
  fwdX: number;
  fwdY: number;
  fwdZ: number;
  upX: number;
  upY: number;
  upZ: number;
}

interface RivalVoice {
  osc: OscillatorNode;
  sub: OscillatorNode;
  filter: BiquadFilterNode;
  gain: GainNode;
  panner: PannerNode;
}

// ── music constants ──
const STEPS_PER_BAR = 16; // 16th notes
const CYCLE_BARS = 16; // intro/verse/lift/drop, 4 bars each
const SECTIONS: readonly MusicSection[] = ['intro', 'verse', 'lift', 'drop'];
const LOOKAHEAD_S = 0.2;
const SCHEDULER_MS = 100;

const SCALES: readonly (readonly number[])[] = [
  [0, 3, 5, 7, 10], // minor pentatonic
  [0, 2, 3, 5, 7, 9, 10], // dorian
  [0, 1, 3, 5, 7, 8, 10], // phrygian
  [0, 2, 3, 5, 7, 8, 10], // aeolian
];
/** Per-bar root movement (semitones from key root), one entry per bar. */
const PROGRESSIONS: readonly (readonly number[])[] = [
  [0, 0, -4, -2],
  [0, -2, -4, -5],
  [0, 3, -2, -4],
  [0, 0, -2, 3],
  [0, -4, -2, -5],
];

// ── positional constants ──
const MAX_RIVAL_VOICES = 4;
const RIVAL_MAX_DIST = 130;

function softClipCurve(drive = 2.2, n = 257): Float32Array<ArrayBuffer> {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = Math.tanh(drive * x);
  }
  return c;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private musicBus!: GainNode;
  private sfxBus!: GainNode;
  private ambientBus!: GainNode;
  private noiseBuf!: AudioBuffer;

  // engine synth
  private engineOsc1!: OscillatorNode;
  private engineOsc2!: OscillatorNode;
  private engineSub!: OscillatorNode;
  private engineSubGain!: GainNode;
  private engineWhine!: OscillatorNode;
  private engineWhineGain!: GainNode;
  private engineDrive!: GainNode;
  private engineLfo!: OscillatorNode;
  private engineFilter!: BiquadFilterNode;
  private engineGain!: GainNode;
  private windNoise!: AudioBufferSourceNode;
  private windGain!: GainNode;
  private windFilter!: BiquadFilterNode;

  // music state
  private duckBus!: GainNode; // pads/bass/arp route here; kicks pump it
  private echoIn!: GainNode;
  private echoDelay!: DelayNode;
  private musicTimer: ReturnType<typeof setInterval> | null = null;
  private musicStep = 0;
  private nextStepTime = 0;
  private stepDur = 60 / 132 / 4;
  private musicSeed = 1;
  private musicMode: MusicMode = 'race';
  private duckFloor = 0.32;
  private scale: readonly number[] = SCALES[0];
  private progression: readonly number[] = PROGRESSIONS[0];
  private keyRootHz = 110;

  // positional rival voices (lazy pool)
  private rivalPool: RivalVoice[] = [];

  unlocked = false;

  constructor(private settings: Settings) {}

  /** Call from a user-gesture handler. Safe to call repeatedly. */
  unlock(): void {
    if (this.unlocked) {
      this.ctx?.resume();
      return;
    }
    try {
      this.ctx = new AudioContext();
    } catch {
      return;
    }
    const ctx = this.ctx;
    this.master = ctx.createGain();
    // gentle bus compression glues music/sfx/engine together
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 20;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.18;
    this.master.connect(comp);
    comp.connect(ctx.destination);
    this.musicBus = ctx.createGain();
    this.sfxBus = ctx.createGain();
    this.ambientBus = ctx.createGain();
    this.musicBus.connect(this.master);
    this.sfxBus.connect(this.master);
    this.ambientBus.connect(this.master);
    this.applyVolumes();

    // shared noise buffer (wind, hats, snares, risers, sfx)
    this.noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

    // sidechain-style duck bus: melodic layers pump against the kick
    this.duckBus = ctx.createGain();
    this.duckBus.gain.value = 1;
    this.duckBus.connect(this.musicBus);
    // dotted-8th echo send for arps (feedback delay, output also ducked)
    this.echoIn = ctx.createGain();
    this.echoDelay = ctx.createDelay(1);
    this.echoDelay.delayTime.value = this.stepDur * 3;
    const echoFb = ctx.createGain();
    echoFb.gain.value = 0.34;
    const echoLevel = ctx.createGain();
    echoLevel.gain.value = 0.4;
    this.echoIn.connect(this.echoDelay);
    this.echoDelay.connect(echoFb);
    echoFb.connect(this.echoDelay);
    this.echoDelay.connect(echoLevel);
    echoLevel.connect(this.duckBus);

    // ── engine: saw+square through drive→waveshaper→lowpass, plus clean sub &
    //    whine layers and a subtle amplitude LFO (tremolo) on the whole stack ──
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    this.engineFilter = ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 300;
    this.engineOsc1 = ctx.createOscillator();
    this.engineOsc1.type = 'sawtooth';
    this.engineOsc1.frequency.value = 55;
    this.engineOsc2 = ctx.createOscillator();
    this.engineOsc2.type = 'square';
    this.engineOsc2.frequency.value = 110.7;
    const engMix = ctx.createGain();
    engMix.gain.value = 0.5;
    this.engineOsc1.connect(engMix);
    this.engineOsc2.connect(engMix);
    this.engineDrive = ctx.createGain();
    this.engineDrive.gain.value = 0.85;
    const shaper = ctx.createWaveShaper();
    shaper.curve = softClipCurve();
    engMix.connect(this.engineDrive);
    this.engineDrive.connect(shaper);
    shaper.connect(this.engineFilter);
    const trem = ctx.createGain();
    trem.gain.value = 1;
    this.engineFilter.connect(trem);
    // sub layer: clean sine an octave down, bypasses drive/filter
    this.engineSub = ctx.createOscillator();
    this.engineSub.type = 'sine';
    this.engineSub.frequency.value = 27.5;
    this.engineSubGain = ctx.createGain();
    this.engineSubGain.gain.value = 0.22;
    this.engineSub.connect(this.engineSubGain);
    this.engineSubGain.connect(trem);
    // whine layer: high sine tracking rpm*4
    this.engineWhine = ctx.createOscillator();
    this.engineWhine.type = 'sine';
    this.engineWhine.frequency.value = 220;
    this.engineWhineGain = ctx.createGain();
    this.engineWhineGain.gain.value = 0;
    this.engineWhine.connect(this.engineWhineGain);
    this.engineWhineGain.connect(trem);
    // amplitude LFO on the series trem node (silent when engineGain is 0)
    this.engineLfo = ctx.createOscillator();
    this.engineLfo.type = 'sine';
    this.engineLfo.frequency.value = 5.2;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 0.05;
    this.engineLfo.connect(lfoDepth);
    lfoDepth.connect(trem.gain);
    trem.connect(this.engineGain);
    this.engineGain.connect(this.ambientBus);
    this.engineOsc1.start();
    this.engineOsc2.start();
    this.engineSub.start();
    this.engineWhine.start();
    this.engineLfo.start();

    // ── wind: filtered noise loop ──
    this.windNoise = ctx.createBufferSource();
    this.windNoise.buffer = this.noiseBuf;
    this.windNoise.loop = true;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.value = 700;
    this.windFilter.Q.value = 0.6;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0;
    this.windNoise.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(this.ambientBus);
    this.windNoise.start();

    this.unlocked = true;
  }

  applyVolumes(): void {
    if (!this.ctx) return;
    this.master.gain.value = this.settings.masterVolume;
    this.musicBus.gain.value = this.settings.musicVolume * 0.5;
    this.sfxBus.gain.value = this.settings.sfxVolume;
    this.ambientBus.gain.value = this.settings.ambientVolume;
  }

  /** Engine + wind follow ship state every frame. */
  updateEngine(speedNorm: number, throttle: number, boosting: boolean, grounded: boolean): void {
    if (!this.ctx || !this.unlocked) return;
    const t = this.ctx.currentTime;
    const rpm = 45 + speedNorm * 160 + (boosting ? 45 : 0);
    this.engineOsc1.frequency.setTargetAtTime(rpm, t, 0.08);
    this.engineOsc2.frequency.setTargetAtTime(rpm * 2.01 + 1, t, 0.08);
    this.engineSub.frequency.setTargetAtTime(Math.max(24, rpm * 0.5), t, 0.08);
    this.engineWhine.frequency.setTargetAtTime(rpm * 4, t, 0.06);
    this.engineWhineGain.gain.setTargetAtTime(
      0.01 + speedNorm * 0.035 + (boosting ? 0.025 : 0),
      t,
      0.1
    );
    // boost pushes the waveshaper harder: distorted edge without a level jump
    this.engineDrive.gain.setTargetAtTime(boosting ? 2.6 : 0.85 + throttle * 0.35, t, 0.07);
    this.engineLfo.frequency.setTargetAtTime(4 + speedNorm * 5, t, 0.2);
    this.engineFilter.frequency.setTargetAtTime(
      240 + speedNorm * 1900 + throttle * 500 + (boosting ? 900 : 0),
      t,
      0.1
    );
    this.engineGain.gain.setTargetAtTime(0.11 + throttle * 0.1 + (boosting ? 0.09 : 0), t, 0.1);
    this.windGain.gain.setTargetAtTime(Math.pow(speedNorm, 1.6) * 0.5 * (grounded ? 1 : 1.25), t, 0.15);
    this.windFilter.frequency.setTargetAtTime(500 + speedNorm * 2600, t, 0.2);
  }

  engineOff(): void {
    if (!this.ctx || !this.unlocked) return;
    const t = this.ctx.currentTime;
    this.engineGain.gain.setTargetAtTime(0, t, 0.2);
    this.windGain.gain.setTargetAtTime(0, t, 0.2);
    for (const v of this.rivalPool) v.gain.gain.setTargetAtTime(0, t, 0.15);
  }

  // ── positional rival engines ──────────────────────────────────────────────

  /**
   * Positional hum for nearby rival craft. Pass every rival each frame; the 4
   * nearest (within earshot) are voiced through a pooled PannerNode each, the
   * rest are silent. Safe no-op before unlock; NaN/odd inputs are ignored.
   */
  updateNearbyShips(ships: NearbyShip[], listener: ListenerPose): void {
    if (!this.ctx || !this.unlocked) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const ok = (v: number) => Number.isFinite(v);
    if (!ok(listener.x) || !ok(listener.y) || !ok(listener.z)) return;
    this.ensureRivalPool();

    const l = ctx.listener;
    if (l.positionX) {
      l.positionX.setTargetAtTime(listener.x, t, 0.03);
      l.positionY.setTargetAtTime(listener.y, t, 0.03);
      l.positionZ.setTargetAtTime(listener.z, t, 0.03);
      if (ok(listener.fwdX) && ok(listener.upX)) {
        l.forwardX.setTargetAtTime(listener.fwdX, t, 0.03);
        l.forwardY.setTargetAtTime(listener.fwdY, t, 0.03);
        l.forwardZ.setTargetAtTime(listener.fwdZ, t, 0.03);
        l.upX.setTargetAtTime(listener.upX, t, 0.03);
        l.upY.setTargetAtTime(listener.upY, t, 0.03);
        l.upZ.setTargetAtTime(listener.upZ, t, 0.03);
      }
    } else if (typeof l.setPosition === 'function') {
      l.setPosition(listener.x, listener.y, listener.z);
      if (ok(listener.fwdX) && ok(listener.upX)) {
        l.setOrientation(listener.fwdX, listener.fwdY, listener.fwdZ, listener.upX, listener.upY, listener.upZ);
      }
    }

    const near = ships
      .filter((s) => ok(s.x) && ok(s.y) && ok(s.z) && ok(s.speed))
      .map((s) => ({
        s,
        d2: (s.x - listener.x) ** 2 + (s.y - listener.y) ** 2 + (s.z - listener.z) ** 2,
      }))
      .filter((e) => e.d2 < RIVAL_MAX_DIST * RIVAL_MAX_DIST)
      .sort((a, b) => a.d2 - b.d2)
      .slice(0, MAX_RIVAL_VOICES);

    for (let i = 0; i < this.rivalPool.length; i++) {
      const v = this.rivalPool[i];
      const e = near[i];
      if (!e) {
        v.gain.gain.setTargetAtTime(0, t, 0.08);
        continue;
      }
      const s = e.s;
      const speedNorm = Math.min(1, Math.max(0, s.speed / 105));
      const rpm = 40 + speedNorm * 150 + (s.boosting ? 40 : 0);
      v.osc.frequency.setTargetAtTime(rpm, t, 0.09);
      v.sub.frequency.setTargetAtTime(Math.max(22, rpm * 0.5), t, 0.09);
      v.filter.frequency.setTargetAtTime(300 + speedNorm * 900 + (s.boosting ? 500 : 0), t, 0.1);
      v.gain.gain.setTargetAtTime(0.05 + speedNorm * 0.04 + (s.boosting ? 0.03 : 0), t, 0.08);
      if (v.panner.positionX) {
        v.panner.positionX.setTargetAtTime(s.x, t, 0.04);
        v.panner.positionY.setTargetAtTime(s.y, t, 0.04);
        v.panner.positionZ.setTargetAtTime(s.z, t, 0.04);
      } else if (typeof v.panner.setPosition === 'function') {
        v.panner.setPosition(s.x, s.y, s.z);
      }
    }
  }

  private ensureRivalPool(): void {
    if (this.rivalPool.length || !this.ctx) return;
    const ctx = this.ctx;
    for (let i = 0; i < MAX_RIVAL_VOICES; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 70;
      const sub = ctx.createOscillator();
      sub.type = 'sine';
      sub.frequency.value = 35;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 600;
      filter.Q.value = 0.8;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      const panner = ctx.createPanner();
      panner.panningModel = 'equalpower'; // cheap; HRTF is overkill for hums
      panner.distanceModel = 'inverse';
      panner.refDistance = 6;
      panner.maxDistance = RIVAL_MAX_DIST + 20;
      panner.rolloffFactor = 1.1;
      osc.connect(filter);
      sub.connect(filter);
      filter.connect(gain);
      gain.connect(panner);
      panner.connect(this.ambientBus);
      osc.start();
      sub.start();
      this.rivalPool.push({ osc, sub, filter, gain, panner });
    }
  }

  // ── one-shot SFX (synthesized, jingles voiced in the current music key) ──
  play(name: SfxName, intensity = 1): void {
    if (!this.ctx || !this.unlocked) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const out = this.sfxBus;
    const env = (peak: number, attack: number, decay: number, dest: AudioNode = out) => {
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t + attack);
      g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
      g.connect(dest);
      return g;
    };
    const tone = (freq: number, type: OscillatorType, peak: number, attack: number, decay: number, slide = 0) => {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.setValueAtTime(freq, t);
      if (slide !== 0) o.frequency.exponentialRampToValueAtTime(Math.max(20, freq + slide), t + attack + decay);
      o.connect(env(peak, attack, decay));
      o.start(t);
      o.stop(t + attack + decay + 0.05);
    };
    const noise = (peak: number, attack: number, decay: number, freq: number, q = 1) => {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = freq;
      f.Q.value = q;
      src.connect(f);
      f.connect(env(peak, attack, decay));
      src.start(t, (t * 5.1) % 1, attack + decay + 0.08);
    };
    /** Ascending run of scale degrees in the current key — never clashes with the music. */
    const jingle = (degs: number[], oct: number, peak: number, gap: number, len: number, type: OscillatorType = 'sine') => {
      degs.forEach((dg, i) => {
        const st = t + i * gap;
        const o = ctx.createOscillator();
        o.type = type;
        o.frequency.value = this.noteHz(dg, oct);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, st);
        g.gain.exponentialRampToValueAtTime(peak, st + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, st + len);
        o.connect(g);
        g.connect(out);
        o.start(st);
        o.stop(st + len + 0.05);
      });
    };

    switch (name) {
      case 'ui_move':
        tone(this.noteHz(0, 3), 'sine', 0.035, 0.004, 0.05);
        break;
      case 'ui_confirm':
        tone(this.noteHz(0, 2), 'sine', 0.06, 0.005, 0.08);
        tone(this.noteHz(4, 2), 'sine', 0.05, 0.04, 0.11);
        break;
      case 'ui_back':
        tone(this.noteHz(0, 1), 'sine', 0.05, 0.005, 0.08);
        break;
      case 'count_beep':
        tone(this.noteHz(0, 1), 'square', 0.1, 0.01, 0.16);
        break;
      case 'count_go':
        tone(this.noteHz(0, 2), 'square', 0.12, 0.01, 0.38);
        tone(this.noteHz(4, 2), 'sine', 0.09, 0.02, 0.48);
        break;
      case 'checkpoint':
        jingle([0, 4], 3, 0.08, 0.05, 0.16);
        noise(0.03, 0.004, 0.08, 6000, 1);
        break;
      case 'lap':
        jingle([0, 2, 4], 2, 0.1, 0.09, 0.24);
        break;
      case 'best_lap':
        jingle([0, 2, 4, 7], 2, 0.1, 0.09, 0.26);
        noise(0.04, 0.01, 0.2, 4500, 0.8);
        break;
      case 'boost_fire':
        noise(0.25, 0.02, 0.5, 900, 0.7);
        tone(120, 'sawtooth', 0.16, 0.02, 0.4, 160);
        break;
      case 'boost_pad':
        tone(320, 'sawtooth', 0.16, 0.01, 0.3, 480);
        noise(0.14, 0.01, 0.25, 1600, 1);
        break;
      case 'wall_hit':
        noise(0.2 * intensity, 0.005, 0.22, 300, 0.8);
        tone(90, 'triangle', 0.2 * intensity, 0.005, 0.16, -35);
        break;
      case 'ship_hit':
        noise(0.24 * intensity, 0.005, 0.3, 480, 0.7);
        tone(70, 'triangle', 0.26 * intensity, 0.005, 0.22, -25);
        break;
      case 'land':
        noise(0.14 * intensity, 0.005, 0.18, 240, 0.9);
        break;
      case 'respawn':
        tone(200, 'sine', 0.14, 0.15, 0.4, 600);
        noise(0.08, 0.1, 0.4, 1200, 0.5);
        break;
      case 'finish':
        // layered victory phrase: scale run + sustained key chord underneath
        jingle([0, 2, 4, 7, 9], 2, 0.11, 0.13, 0.55);
        for (const dg of [0, 2, 4]) {
          const o = ctx.createOscillator();
          o.type = 'triangle';
          o.frequency.value = this.noteHz(dg, 1);
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(0.055, t + 0.15);
          g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
          o.connect(g);
          g.connect(out);
          o.start(t);
          o.stop(t + 1.5);
        }
        break;
      case 'eliminated':
        tone(340, 'sawtooth', 0.16, 0.01, 0.5, -240);
        break;
      case 'draft':
        noise(0.05, 0.05, 0.3, 2400, 0.4);
        break;
    }
  }

  // ── generative soundtrack ─────────────────────────────────────────────────

  /**
   * Start the soundtrack. Seed picks the key (scale/root/progression)
   * deterministically. Mode defaults from tempo: below 124bpm it plays the
   * mellow menu arrangement, otherwise the full race cycle.
   */
  startMusic(seed = 1, bpm = 132, mode?: MusicMode): void {
    if (!this.ctx || !this.unlocked) return;
    this.stopMusic();
    this.musicSeed = Math.max(1, Math.floor(seed) >>> 0);
    this.musicMode = mode ?? (bpm < 124 ? 'menu' : 'race');
    this.musicStep = 0;
    this.stepDur = 60 / bpm / 4; // 16ths
    this.duckFloor = this.musicMode === 'menu' ? 0.72 : 0.32;
    this.scale = SCALES[Math.floor(this.hash(11) * SCALES.length)];
    this.progression = PROGRESSIONS[Math.floor(this.hash(23) * PROGRESSIONS.length)];
    this.keyRootHz = 110 * Math.pow(2, (Math.floor(this.hash(37) * 8) - 4) / 12);
    this.echoDelay.delayTime.value = this.stepDur * 3; // dotted 8th
    this.nextStepTime = this.ctx.currentTime + 0.05;
    this.schedulerTick();
    this.musicTimer = setInterval(() => this.schedulerTick(), SCHEDULER_MS);
  }

  stopMusic(): void {
    if (this.musicTimer) clearInterval(this.musicTimer);
    this.musicTimer = null;
    if (this.ctx && this.unlocked) {
      const g = this.duckBus.gain;
      g.cancelScheduledValues(this.ctx.currentTime);
      g.setValueAtTime(1, this.ctx.currentTime);
    }
  }

  /** Lookahead scheduler: fires ~10Hz, schedules 16ths on the audio clock. */
  private schedulerTick(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    // if the timer was throttled (background tab) skip past missed steps
    while (this.nextStepTime < ctx.currentTime - 0.05) {
      this.musicStep++;
      this.nextStepTime += this.stepDur;
    }
    const horizon = ctx.currentTime + LOOKAHEAD_S;
    let guard = 0;
    while (this.nextStepTime < horizon && guard++ < 64) {
      const step = this.musicStep++;
      const bar = Math.floor(step / STEPS_PER_BAR);
      const beat = step % STEPS_PER_BAR;
      if (this.musicMode === 'menu') this.scheduleMenuStep(bar, beat, this.nextStepTime);
      else this.scheduleRaceStep(bar, beat, this.nextStepTime);
      this.nextStepTime += this.stepDur;
    }
  }

  /** Race arrangement: 16-bar cycle of intro → verse → lift → drop. */
  private scheduleRaceStep(bar: number, beat: number, t: number): void {
    const cycleBar = bar % CYCLE_BARS;
    const section = SECTIONS[Math.floor(cycleBar / 4)];
    const chordRoot = this.chordRootHz(bar);
    const sectionEndBar = cycleBar % 4 === 3;

    // drums
    if (section === 'intro') {
      if (beat === 0 || beat === 8) this.mKick(t, 0.34);
      if (beat % 4 === 2) this.mHat(t, 0.05, 0.04);
    } else {
      if (beat % 4 === 0) this.mKick(t, section === 'drop' ? 0.55 : 0.48);
      if (beat % 2 === 1) this.mHat(t, section === 'drop' ? 0.12 : 0.08, 0.05);
      if (section === 'drop' && beat % 8 === 6) this.mHat(t, 0.09, 0.16, true);
      if ((section === 'lift' || section === 'drop') && (beat === 4 || beat === 12)) {
        this.mSnare(t, section === 'drop' ? 0.2 : 0.15);
      }
      if (section === 'drop' && beat === 14 && this.hash(bar * 3 + 7) < 0.35) this.mKick(t, 0.3);
    }

    // bass (ducked against the kick)
    const bassHz = chordRoot / 2;
    if (section === 'intro') {
      if (beat === 0) this.mVoice(t, bassHz, 'triangle', 0.16, 0.02, this.stepDur * 12, this.duckBus);
    } else if (section === 'verse') {
      if (beat === 0 || beat === 7 || beat === 10) {
        this.mVoice(t, bassHz, 'sawtooth', 0.17, 0.01, 0.32, this.duckBus);
      }
    } else if (section === 'lift') {
      if (beat % 2 === 0) this.mVoice(t, bassHz, 'sawtooth', 0.15, 0.01, 0.16, this.duckBus);
    } else {
      // drop: offbeat 8ths pump between the kicks, sub reinforces bar starts
      if (beat % 4 === 2) this.mVoice(t, bassHz, 'sawtooth', 0.2, 0.01, 0.24, this.duckBus);
      if (beat === 0) this.mVoice(t, bassHz / 2, 'sine', 0.18, 0.01, 0.42, this.duckBus);
    }

    // arp: 4-bar melodic phrase, density and octave rise with the section
    const density = section === 'intro' ? 0.22 : section === 'verse' ? 0.5 : section === 'lift' ? 0.68 : 0.85;
    if (this.hash(bar * STEPS_PER_BAR + beat + 501) < density) {
      const deg = Math.floor(this.hash(beat * 5 + (bar % 4) * 29 + 101) * (this.scale.length + 3));
      const oct = section === 'drop' && beat % 8 === 4 ? 3 : 2;
      const type: OscillatorType =
        section === 'intro' ? 'triangle' : this.hash(beat * 3 + 53) < 0.5 ? 'square' : 'sawtooth';
      const pan = ((beat % 4) / 3) * 1.4 - 0.7;
      this.mVoice(t, this.noteHz(deg, oct), type, section === 'intro' ? 0.032 : 0.048, 0.008, 0.16, this.duckBus, pan, 0.35);
    }

    // pads at bar starts (drop swaps pads for stabs)
    if (beat === 0 && section !== 'drop') {
      const every = section === 'lift' ? 1 : 2;
      if (cycleBar % every === 0) this.mChord(t, chordRoot, 'triangle', 0.045, 0.4, 1.9);
    }
    if (section === 'drop' && (beat === 0 || beat === 7)) {
      this.mChord(t, chordRoot * 2, 'sawtooth', 0.045, 0.01, 0.22);
    }

    // transitions: riser through the last bar of the lift (into the drop),
    // occasional riser into the next cycle, snare fills at section ends
    if (beat === 0 && cycleBar === 11) this.mRiser(t, this.stepDur * STEPS_PER_BAR);
    if (beat === 0 && cycleBar === 15 && this.hash(bar + 977) < 0.6) {
      this.mRiser(t, this.stepDur * STEPS_PER_BAR);
    }
    if (beat === 12 && sectionEndBar && this.hash(bar * 7 + 31) < 0.55) this.mFill(t);
  }

  /** Menu arrangement: mellow — soft half-time pulse, warm pads, sparse bells. */
  private scheduleMenuStep(bar: number, beat: number, t: number): void {
    const chordRoot = this.chordRootHz(bar);
    if (beat === 0 || beat === 8) this.mKick(t, 0.28);
    if (beat % 4 === 2) this.mHat(t, 0.03, 0.03);
    if (beat === 0) this.mVoice(t, chordRoot / 2, 'sine', 0.17, 0.06, this.stepDur * 14, this.duckBus);
    if (beat === 0 && bar % 2 === 0) this.mChord(t, chordRoot, 'triangle', 0.05, 0.6, 3.2);
    if (this.hash(bar * STEPS_PER_BAR + beat + 501) < 0.16) {
      const deg = Math.floor(this.hash(beat * 5 + (bar % 8) * 29 + 101) * (this.scale.length + 2));
      const pan = ((beat % 4) / 3) * 1.2 - 0.6;
      this.mVoice(t, this.noteHz(deg, 2), 'sine', 0.045, 0.01, 0.6, this.duckBus, pan, 0.55);
    }
  }

  // ── music instruments (all scheduled at exact AudioContext times) ──

  /** Kick + sidechain pump: duck bus drops on the hit, recovers over a beat. */
  private mKick(t: number, level: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(150, t);
    o.frequency.exponentialRampToValueAtTime(40, t + 0.11);
    const g = ctx.createGain();
    g.gain.setValueAtTime(level, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
    o.connect(g);
    g.connect(this.musicBus);
    o.start(t);
    o.stop(t + 0.3);
    const d = this.duckBus.gain;
    d.setValueAtTime(this.duckFloor, t);
    d.linearRampToValueAtTime(1, t + this.stepDur * 4 * 0.85);
  }

  private mHat(t: number, peak: number, decay: number, open = false): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.playbackRate.value = open ? 0.9 : 1.4;
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = open ? 7000 : 8500;
    const g = ctx.createGain();
    g.gain.setValueAtTime(peak, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
    src.connect(f);
    f.connect(g);
    g.connect(this.musicBus);
    src.start(t, (t * 13.7) % 1.2, decay + 0.05);
  }

  private mSnare(t: number, peak: number): void {
    this.mHit(t, 1800, peak, 0.14);
    this.mVoice(t, 190, 'triangle', peak * 0.7, 0.004, 0.09, this.musicBus);
  }

  /** Filtered noise hit (snare body, fill toms). */
  private mHit(t: number, freq: number, peak: number, decay: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = freq;
    f.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(peak, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
    src.connect(f);
    f.connect(g);
    g.connect(this.musicBus);
    src.start(t, (t * 7.3) % 1.5, decay + 0.05);
  }

  /** Enveloped oscillator voice with optional stereo pan and echo send. */
  private mVoice(
    t: number,
    freq: number,
    type: OscillatorType,
    peak: number,
    attack: number,
    decay: number,
    dest: AudioNode,
    pan = 0,
    echo = 0
  ): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t + Math.max(0.004, attack));
    g.gain.exponentialRampToValueAtTime(0.0001, t + Math.max(0.004, attack) + decay);
    o.connect(g);
    let out: AudioNode = g;
    if (pan !== 0) {
      const p = ctx.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, pan));
      g.connect(p);
      out = p;
    }
    out.connect(dest);
    if (echo > 0) {
      const e = ctx.createGain();
      e.gain.value = echo;
      out.connect(e);
      e.connect(this.echoIn);
    }
    o.start(t);
    o.stop(t + Math.max(0.004, attack) + decay + 0.05);
  }

  /** Triad built from the current scale, transposed to the chord root. */
  private mChord(t: number, rootHz: number, type: OscillatorType, peak: number, attack: number, decay: number): void {
    const sc = this.scale;
    const ivs = [0, sc[2 % sc.length], sc[4 % sc.length]];
    for (let i = 0; i < ivs.length; i++) {
      const pan = (i - 1) * 0.35;
      this.mVoice(t, rootHz * Math.pow(2, ivs[i] / 12), type, peak, attack, decay, this.duckBus, pan);
    }
  }

  /** Noise sweep + rising tone leading into a section change. */
  private mRiser(t: number, dur: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = 1.1;
    f.frequency.setValueAtTime(400, t);
    f.frequency.exponentialRampToValueAtTime(6500, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.14, t + dur);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.09);
    src.connect(f);
    f.connect(g);
    g.connect(this.musicBus);
    src.start(t);
    src.stop(t + dur + 0.12);
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(this.keyRootHz * 2, t);
    o.frequency.exponentialRampToValueAtTime(this.keyRootHz * 4, t + dur);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, t);
    og.gain.exponentialRampToValueAtTime(0.045, t + dur);
    og.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.08);
    o.connect(og);
    og.connect(this.musicBus);
    o.start(t);
    o.stop(t + dur + 0.1);
  }

  /** Four rising 16th noise hits filling the last beat of a section. */
  private mFill(t: number): void {
    for (let i = 0; i < 4; i++) {
      this.mHit(t + i * this.stepDur, 900 + i * 500, 0.07 + i * 0.02, 0.09);
    }
  }

  // ── music theory helpers ──

  /** Deterministic [0,1) from the music seed and a salt. */
  private hash(n: number): number {
    let x = (this.musicSeed * 374761393 + n * 668265263) >>> 0;
    x = (x ^ (x >>> 13)) >>> 0;
    x = (x * 1274126177) >>> 0;
    return ((x >>> 8) & 0xffff) / 0x10000;
  }

  /** Frequency of a scale degree in the current key (degree wraps octaves). */
  private noteHz(degree: number, octave = 0): number {
    const sc = this.scale;
    const n = sc.length;
    const idx = ((degree % n) + n) % n;
    const oct = octave + Math.floor(degree / n);
    return this.keyRootHz * Math.pow(2, (sc[idx] + 12 * oct) / 12);
  }

  private chordRootHz(bar: number): number {
    return this.keyRootHz * Math.pow(2, this.progression[bar % this.progression.length] / 12);
  }

  suspend(): void {
    this.ctx?.suspend();
  }

  resume(): void {
    this.ctx?.resume();
  }
}
